import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import type { FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import type { EFontStyle, EMenuLogoPosition } from '@common/enums';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { ReplacedFile } from '@components/sp-file-manager/src/types';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import type { MenuItem, MenuWidgetData, SubmenuItem } from '@web/widgets/menu/type';

import { moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { EButtonLinkType, EHorizontalPosition, WidgetsDataTypes } from '@common/enums';
import { extendWebFileDataPromise } from '@common/helpers';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { StyleSettingsComponent } from './style-settings/style-settings.component';
import { MenuSettingsComponent } from '@web/widgets/menu/components/menu-control/menu-settings/menu-settings.component';
import { LogoSettingsComponent } from '@web/widgets/menu/components/menu-control/logo-settings/logo-settings.component';
import { EControlHandleEvents } from '@ui/sp-control-handle/src/sp-control-handle.enum';
import { createId } from '@paralleldrive/cuid2';
import { environment } from '@web-env/environment';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';

@Component({
    selector: 'web-menu-control',
    templateUrl: './menu-control.component.html',
    styleUrls: ['./menu-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        SpFileManagerComponentModule,
        TooltipModule,
        DragDropModule,
        StyleSettingsComponent,
        MenuSettingsComponent,
        LogoSettingsComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
    ],
})
export class MenuControlComponent extends WebBaseControlComponent<MenuWidgetData> {
    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);

    public readonly ERadioGroupType = ERadioGroupType;
    public readonly FONT_SIZE_MAX = 72;
    public readonly EHorizontalPosition = EHorizontalPosition;
    public readonly DirectoryItemCategory = DirectoryItemCategory;
    public readonly LOGO_MAX_WIDTH = 320;

    public currentPanel = 'main';

    public editableMenuItem: MenuItem = null;
    public editableMenuItemIndex: number = null;
    public editableSubmenuItem: SubmenuItem = null;
    public editableSubmenuItemIndex: number = null;
    public buttonDisabled: boolean = false;
    public editableLogo = null;
    public uploadImageWidth: number;
    public editableMenuStyle: MenuWidgetData = null;
    public readonly fmSourceType = FmSourceType;

    public trackIndex = (index: number): number => index;

    public controlHandleEvents = EControlHandleEvents;

    public readonly userId$ = this.userFacade.userId$;

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: this.fb.group({
                isLogo: null,
                logoWidth: null,
                uploadImage: null,
                logoPosition: null,
                menuList: this.fb.array([]),
                textColor: null,
                submenuTextColor: null,
                themeTextColor: null,
                menuItemIndent: null,
                horizontalPosition: null,
                layoutDirection: null,
                fontStile: this.fb.array([]),
                submenuFontStile: this.fb.array([]),
                fontSize: null,
                submenuFontSize: null,
                submenuListBackground: null,
                showIconOnMobile: null,
                iconColor: null,
                iconPosition: null,
                iconSize: null,
                hoverEffects: this.fb.group({
                    type: null,
                    color: null,
                    transparency: null,
                    lineType: null,
                    lineWidth: null,
                    animationType: null,
                }),
                isSynchronizeStyle: null,
                hideOnMobile: null,
                hideOnTablet: null,
                hideOnDesktop: null,
                position: this.fb.group({
                    x: null,
                    tabletX: null,
                    mobileX: null,
                    y: null,
                    tabletY: null,
                    mobileY: null,
                    rotate: null,
                    tabletRotate: null,
                    mobileRotate: null,
                    zIndex: null,
                    tabletZIndex: null,
                    mobileZIndex: null,
                    width: null,
                    tabletWidth: null,
                    mobileWidth: null,
                }),
            }),
            styles: null,
        });
    }

    protected setFormData(): void {
        this.menuList.clear({ emitEvent: false });
        this.fontStile.clear({ emitEvent: false });
        this.submenuFontStile.clear({ emitEvent: false });
        this.widgetData.menuList.forEach((menuItem: MenuItem) =>
            this.menuList.push(this.fb.control(menuItem), { emitEvent: false })
        );
        this.widgetData.fontStile.forEach((item: EFontStyle) =>
            this.fontStile.push(this.fb.control(item), { emitEvent: false })
        );
        this.widgetData.submenuFontStile.forEach((item: EFontStyle) =>
            this.submenuFontStile.push(this.fb.control(item), { emitEvent: false })
        );
        this.controlForm.patchValue(
            {
                data: { ...this.widgetData },
                styles: { ...this.widgetStyles },
            },
            { emitEvent: false }
        );

        if (this.widgetData?.uploadImage?.imageWidth) {
            this.uploadImageWidth = this.widgetData.uploadImage.imageWidth;
        }

        this.editableMenuStyle = this.widgetData;
    }

    public setSynchronizeStyle() {
        this.isSynchronizeStyle.setValue(!this.isSynchronizeStyle.value);
    }

    public get logoMaxWidthValue(): number {
        if (this.widgetData?.uploadImage?.imageWidth > this.LOGO_MAX_WIDTH) {
            return this.LOGO_MAX_WIDTH;
        }

        return this.widgetData.uploadImage.imageWidth;
    }

    public get refreshButtonState(): boolean {
        return (
            (this.logoWidth.value < this.uploadImageWidth && this.uploadImageWidth < this.LOGO_MAX_WIDTH) ||
            (this.logoWidth.value < this.LOGO_MAX_WIDTH && this.uploadImageWidth > this.LOGO_MAX_WIDTH)
        );
    }

    public get data(): UntypedFormControl {
        return this.controlForm.get('data') as UntypedFormControl;
    }

    public get styles(): UntypedFormControl {
        return this.controlForm.get('styles') as UntypedFormControl;
    }

    public get isLogo(): FormControl<boolean> {
        return this.data.get('isLogo') as FormControl<boolean>;
    }

    public get logoWidth(): FormControl<number> {
        return this.data.get('logoWidth') as FormControl<number>;
    }

    public get uploadImage(): FormControl<SelectedFileExtended> {
        return this.data.get('uploadImage') as FormControl<SelectedFileExtended>;
    }

    public get logoPosition(): FormControl<EMenuLogoPosition> {
        return this.data.get('logoPosition') as FormControl<EMenuLogoPosition>;
    }

    public get menuList(): UntypedFormArray {
        return this.data.get('menuList') as UntypedFormArray;
    }

    public get horizontalPosition(): FormControl<EHorizontalPosition> {
        return this.data.get('horizontalPosition') as FormControl<EHorizontalPosition>;
    }

    public get iconPosition(): FormControl<EHorizontalPosition> {
        return this.data.get('iconPosition') as FormControl<EHorizontalPosition>;
    }

    public get fontStile(): UntypedFormArray {
        return this.data.get('fontStile') as UntypedFormArray;
    }

    public get submenuFontStile(): UntypedFormArray {
        return this.data.get('submenuFontStile') as UntypedFormArray;
    }

    public get position(): any {
        return this.data.get('position') as FormGroup<any>;
    }

    public get hideOnMobile(): FormControl<boolean> {
        return this.data.get('hideOnMobile') as FormControl<boolean>;
    }

    public get hideOnTablet(): FormControl<boolean> {
        return this.data.get('hideOnTablet') as FormControl<boolean>;
    }

    public get hideOnDesktop(): FormControl<boolean> {
        return this.data.get('hideOnDesktop') as FormControl<boolean>;
    }

    public get isSynchronizeStyle(): FormControl<boolean> {
        return this.data.get('isSynchronizeStyle') as FormControl<boolean>;
    }

    public addMenuItem(): void {
        this.menuList.push(
            this.fb.control({
                name: 'Menu item',
                value: 'https://example.com',
                linkType: EButtonLinkType.link,
                isVisible: true,
                id: createId(),
            })
        );
    }

    public copyMenuItem(menuItem: MenuItem, index: number): void {
        const newMenuItem: MenuItem = {
            ...menuItem,
            id: createId(),
        };

        this.menuList.insert(index + 1, this.fb.control(newMenuItem));

        this.changeDetectorRef.detectChanges();
    }

    public copySubmenuItem(parentMenuItem: MenuItem, submenuItem: SubmenuItem, index: number): void {
        const newSubmenuItem: MenuItem = {
            ...submenuItem,
            id: createId(),
        };

        const newSubmenuList = [...parentMenuItem.submenuList];
        newSubmenuList.splice(index, 0, newSubmenuItem);

        const parentIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.menuList.at(parentIndex).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public addSubmenuItem(index: number): void {
        const submenuItem: MenuItem = {
            name: 'Submenu item',
            linkUrl: 'https://example.com',
            linkType: EButtonLinkType.link,
            isVisible: true,
            id: createId(),
        };

        const newSubmenuList: SubmenuItem[] = [];

        const menuItem = this.menuList.at(index).value;

        if (!menuItem.submenuList?.length) {
            newSubmenuList.push(submenuItem);
        } else {
            for (let i = 0; i < menuItem.submenuList?.length; i++) {
                newSubmenuList.push(menuItem.submenuList[i]);
            }

            newSubmenuList.push(submenuItem);
        }

        this.menuList.at(index).patchValue({
            ...menuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public removeMenuItem(i: number) {
        if (this.menuList.value.length === 1) {
            return;
        }

        this.menuList.removeAt(i);
    }

    public removeSubmenuItem(parentMenuItem: MenuItem, i: number) {
        const newSubmenuList = [...parentMenuItem.submenuList];
        newSubmenuList.splice(i, 1);

        const parentIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.menuList.at(parentIndex).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public changeMenuItemVisibility(index: number) {
        const menuItem = this.menuList.at(index).value;
        this.menuList.at(index).patchValue({
            ...menuItem,
            ...{ isVisible: !menuItem.isVisible },
        });
    }

    public changeSubmenuItemVisibility(parentMenuItem: MenuItem, submenuItem: SubmenuItem, i: number) {
        const newSubmenuItem: SubmenuItem = {
            ...submenuItem,
            isVisible: !submenuItem.isVisible,
        };

        const newSubmenuList = [...parentMenuItem.submenuList];
        newSubmenuList.splice(i, 1, newSubmenuItem);

        const parentIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.menuList.at(parentIndex).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public editMenuItem(menuItem: MenuItem, index: number): void {
        this.editableMenuItem = menuItem;
        this.editableMenuItemIndex = index;

        this.currentPanel = 'item-settings';
    }

    public editSubmenuItem(parentMenuItem: MenuItem, submenuItem: SubmenuItem, submenuIndex: number): void {
        this.editableMenuItem = parentMenuItem;
        this.editableSubmenuItem = submenuItem;
        this.editableSubmenuItemIndex = submenuIndex;
        this.editableMenuItemIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.currentPanel = 'submenu-item-settings';
    }

    public toggleLogo(value: boolean): void {
        this.isLogo.setValue(value);
        if (value) {
            this.horizontalPosition.setValue(EHorizontalPosition.R);
            this.iconPosition.setValue(EHorizontalPosition.R);
        } else {
            this.horizontalPosition.setValue(EHorizontalPosition.L);
            this.iconPosition.setValue(EHorizontalPosition.L);
        }
    }

    public setLogoWidth(value: number): void {
        this.logoWidth.setValue(value);
    }

    public async setUploadImage(file: SelectedFileExtended): Promise<void> {
        this.buttonDisabled = true;

        const extendedFile = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.uploadImage.setValue(extendedFile, { emitEvent: false });
        this.uploadImageWidth = extendedFile.imageWidth;
        if (extendedFile.imageWidth > this.LOGO_MAX_WIDTH) {
            this.setLogoWidth(this.LOGO_MAX_WIDTH);
        } else {
            this.setLogoWidth(extendedFile.imageWidth);
        }

        this.buttonDisabled = false;
    }

    public async replaceUploadImage({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled = true;

        const extendedFile = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.uploadImage.setValue(extendedFile, { emitEvent: false });
        this.uploadImageWidth = extendedFile.imageWidth;
        if (extendedFile.imageWidth > this.LOGO_MAX_WIDTH) {
            this.setLogoWidth(this.LOGO_MAX_WIDTH);
        } else {
            this.setLogoWidth(extendedFile.imageWidth);
        }

        this.buttonDisabled = false;
    }

    openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({ widgetId: this.widget.id, dataType: WidgetsDataTypes.uploadImage });
    }

    public refreshImageWidth(): void {
        if (this.uploadImageWidth < this.LOGO_MAX_WIDTH) {
            this.setLogoWidth(this.uploadImageWidth);
        } else {
            this.setLogoWidth(this.LOGO_MAX_WIDTH);
        }
    }

    public editImage(logo: SelectedFileInterface): void {
        this.editableLogo = logo;
        this.currentPanel = 'logo-settings';
    }

    public removeFile(): void {
        this.uploadImage.setValue(null);
    }

    public setHorizontalPosition(value: EHorizontalPosition): void {
        this.horizontalPosition.setValue(value);
    }

    public saveMenuItem(data: MenuItem): void {
        this.menuList.at(this.editableMenuItemIndex).patchValue(data);
    }

    public saveSubmenuItem(submenuData: SubmenuItem): void {
        const newSubmenuList = [...this.editableMenuItem.submenuList];
        newSubmenuList.splice(this.editableSubmenuItemIndex, 1, submenuData);

        this.menuList.at(this.editableMenuItemIndex).patchValue({
            ...this.editableMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public dropMenuItem(event: CdkDragDrop<FormArray>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const buttons = [...this.menuList.value];
        moveItemInArray(buttons, event.previousIndex, event.currentIndex);

        this.menuList.setValue(buttons);
    }

    public dropSubmenuItem(event: CdkDragDrop<FormArray>, parentMenuItem: MenuItem, index): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const newSubmenuList = [...parentMenuItem.submenuList];
        moveItemInArray(newSubmenuList, event.previousIndex, event.currentIndex);

        this.menuList.at(index).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public editMenuStyle(): void {
        this.currentPanel = 'style-settings';
    }

    public saveMenuStyle(data: MenuWidgetData): void {
        this.data.patchValue(data);
        this.updateWidgetData({
            fontStile: data.fontStile,
            submenuFontStile: data.submenuFontStile,
            hoverEffects: data.hoverEffects,
        });
    }

    public saveLogoSettings(data: SelectedFileExtended): void {
        this.updateWidgetData({ uploadImage: { ...this.uploadImage.value, ...data } });
    }

    public cancelEditSettings(): void {
        this.currentPanel = 'main';
    }

    public showEditImageModal() {
        this.showImageEditor(this.uploadImage.value, WidgetsDataTypes.uploadImage);
    }

    public get imageOwnerUserId(): number {
        const urlParts = this.uploadImage.value.url.split('/');
        return Number(urlParts[1].substring(32));
    }
}
